import { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

type AppRole = 'admin' | 'moderator' | 'user';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  checkRole: (role: AppRole) => Promise<boolean>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const manualSignOutRef = useRef(false);

  // We must not decide routing until we know both the auth session and role.
  const [authReady, setAuthReady] = useState(false);
  const [roleReady, setRoleReady] = useState(false);
  const loading = !(authReady && roleReady);

  const checkIfAdmin = async (userId: string) => {
    try {
      // Use the has_role RPC function which bypasses RLS safely
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: userId,
        _role: 'admin'
      });

      if (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        return;
      }

      setIsAdmin(!!data);
    } catch (err) {
      console.error('Error checking admin status:', err);
      setIsAdmin(false);
    }
  };

  const checkRole = async (role: AppRole): Promise<boolean> => {
    if (!user) return false;
    
    try {
      // Use the has_role RPC function which bypasses RLS safely
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: user.id,
        _role: role
      });

      if (error) {
        console.error('Error checking role:', error);
        return false;
      }

      return !!data;
    } catch (err) {
      console.error('Error checking role:', err);
      return false;
    }
  };

  const syncSessionState = async (nextSession: Session | null) => {
    setSession(nextSession);
    setUser(nextSession?.user ?? null);
    setAuthReady(true);

    if (nextSession?.user) {
      setRoleReady(false);
      await checkIfAdmin(nextSession.user.id);
      setRoleReady(true);
      return;
    }

    setIsAdmin(false);
    setRoleReady(true);
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (event === 'SIGNED_OUT' && !manualSignOutRef.current) {
        // Recover from transient sign-out events before forcing logout
        setTimeout(async () => {
          const { data: { session: recoveredSession } } = await supabase.auth.getSession();
          await syncSessionState(recoveredSession ?? null);
          if (recoveredSession) {
            manualSignOutRef.current = false;
          }
        }, 0);
        return;
      }

      if (event !== 'SIGNED_OUT') {
        manualSignOutRef.current = false;
      }

      setTimeout(() => {
        void syncSessionState(nextSession ?? null);
      }, 0);
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      void syncSessionState(existingSession ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Keep-alive: refresh session periodically AND when user returns to tab
  useEffect(() => {
    if (!session) return;

    const refreshSession = async () => {
      try {
        const { data: { session: refreshed } } = await supabase.auth.getSession();
        if (!refreshed) {
          // Try to refresh the token explicitly
          await supabase.auth.refreshSession();
        }
      } catch (e) {
        console.warn('Session refresh failed:', e);
      }
    };

    // Periodic keep-alive every 2 minutes
    const keepAliveInterval = setInterval(refreshSession, 2 * 60 * 1000);

    // Refresh when user returns to the tab after being away
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSession();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(keepAliveInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [session?.access_token]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error };
  };

  const signOut = async () => {
    manualSignOutRef.current = true;
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAdmin,
        loading,
        signIn,
        signUp,
        signOut,
        checkRole,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
